import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useInstanzErkennung } from '@/composables/useInstanzErkennung'
import { usePermissionsStore } from '@/stores/permissions'

export interface NavigationItem {
  text: string
  uniqueName?: string
  icon?: RegisteredIcon | null
  color?: string
  path?: string
  url?: string
  action?: () => void
  replace?: boolean
  number?: string
  target?: string
  disabled?: boolean
  addurl?: string
  disableTranslation?: boolean
  permissionName?: string
  /** Zweites Recht, das derselbe Eintrag zusaetzlich verlangt - fuer Eintraege, die ein Modul in
   * einem anderen zeigen. */
  additionalPermissionName?: string
  needsWritePermission?: boolean
  nurFuerAsixSystentBenutzer?: boolean
  children?: NavigationItem[]
}

export interface OptionalNavigationItem extends NavigationItem {
  name: string
  badge?: string | null
  action?: () => void
  additionalAction?: (() => void) | null
  additionalActionTooltip?: string
  visibleFullPath: string
  replace?: boolean
  order: number
  children?: OptionalNavigationItem[]
}

export interface NavigationDivider {
  divider: boolean
}

export type NavigationPosition = {
  x: number
  y: number
  count: number
}

export type Navigation =
  | { type: 'item' } & NavigationItem
  | { type: 'divider' } & NavigationDivider

export type OptionalNavigation =
  | { type: 'item' } & OptionalNavigationItem
  | { type: 'divider' } & NavigationDivider


export const EINZELN_FREIGESCHALTETE_PROJEKTE_DOMAINS = [
  'aboka.asix.pro',
  'gd.asix.pro',
  'ps.asix.pro',
  'mm.asix.pro',
]

export const useNavigationStore = defineStore('navigation', () => {
  // State
  const navigationVisible = ref(true)
  const navigationItems = ref<Navigation[]>([])
  const optionalNavigationItems = ref<OptionalNavigation[]>([])
  const lastPosition = ref<Record<string, NavigationPosition>>({})
  const browserBackForwardUsed = ref(false)

  // Actions
  function setNavigationVisible(payload: boolean) {
    navigationVisible.value = payload
  }

  function isNavigationItemVisible(
    navigationItem: NavigationItem,
    erkennung = useInstanzErkennung(),
  ): boolean {
    if (navigationItem.nurFuerAsixSystentBenutzer) {
      const { isDemoOrPresentationInstanz, isProjektInstanz, isDevInstanz } = erkennung
      if (!isDemoOrPresentationInstanz) {
        // Einzeln freigeschaltete Instanzen dürfen das Modul regulär sehen (keine Demo-/Praesentations-Instanz)
        if (EINZELN_FREIGESCHALTETE_PROJEKTE_DOMAINS.includes(localStorage.getItem('domain') ?? ''))
          return true
        const erlaubteDomains = ['asix.asix.pro', 'systent.asix.pro', 'systent-gruppe.asix.pro']
        const domainErlaubt = erlaubteDomains.includes(localStorage.getItem('domain') ?? '') ||
          isProjektInstanz ||
          isDevInstanz
        const istSystentBenutzer = domainErlaubt &&
          localStorage.getItem('is_systent_user') === 'true'
        if (!istSystentBenutzer) return false
      }
    }

    return true
  }

  /**
   * Die Rechte eines Eintrags, fuer alle drei Ebenen (Gruppe, Kind, Unterkind) an einer Stelle -
   * sonst gilt ein neues Feld nur dort, wo es jemand eingebaut hat. `needsWritePermission`
   * ersetzt die Lese- durch die Schreibpruefung.
   */
  function hasNavigationPermission(navigationItem: NavigationItem): boolean {
    const permissionsStore = usePermissionsStore()
    const granted = (permissionName: string) => navigationItem.needsWritePermission
      ? permissionsStore.hasChangePermission(permissionName)
      : permissionsStore.hasViewPermission(permissionName)

    if (navigationItem.permissionName && !granted(navigationItem.permissionName)) return false
    return !navigationItem.additionalPermissionName
      || granted(navigationItem.additionalPermissionName)
  }

  function replaceNavigationItems(payload: NavigationItem[]) {
    const erkennung = useInstanzErkennung()
    const items: (NavigationItem & { type: 'item' })[] = []
    for (const navigationItem of payload) {
      if (!isNavigationItemVisible(navigationItem, erkennung)) continue
      if (!hasNavigationPermission(navigationItem)) continue
      const children: NavigationItem[] = []
      if (navigationItem.children) {
        for (const child of navigationItem.children) {
          if (!isNavigationItemVisible(child, erkennung)) continue
          const subChildren: NavigationItem[] = []
          if (child.children) {
            for (const subChild of child.children) {
              if (!isNavigationItemVisible(subChild, erkennung)) continue
              if (hasNavigationPermission(subChild)) subChildren.push(subChild)
            }
            child.children = subChildren
            if (subChildren.length === 0) continue
            children.push(child)
          } else {
            if (hasNavigationPermission(child)) children.push(child)
          }
        }
        navigationItem.children = children
        if (children.length === 0) continue
      }
      items.push({ type: 'item', ...navigationItem })
    }
    navigationItems.value = items
  }

  function addOptionalNavigationItem(payload: OptionalNavigationItem) {
    const index = optionalNavigationItems.value.findIndex(
      el => (el as OptionalNavigationItem).name === payload.name
    )
    if (index === -1) {
      if ('order' in payload) {
        optionalNavigationItems.value.splice(payload.order, 0, { type: 'item', ...payload })
      } else {
        optionalNavigationItems.value.push(payload)
      }
    } else {
      optionalNavigationItems.value[index] = { type: 'item', ...payload }
    }
  }

  function removeOptionalNavigationItem(payload: { uniqueName: string }) {
    optionalNavigationItems.value = optionalNavigationItems.value.filter(item => (item as OptionalNavigationItem).name !== payload.uniqueName)
  }

  function clearOptionalNavigationItems() {
    optionalNavigationItems.value = []
  }

  function setOptionalNavigationItemData(payload: { name: string } & Partial<OptionalNavigationItem>) {
    const index = optionalNavigationItems.value.findIndex(
      el => (el as OptionalNavigationItem).name === payload.name
    )
    if (index !== -1) {
      Object.keys(payload).forEach(key => {
        if (payload[key]) {
          optionalNavigationItems.value[index][key] = payload[key]
        }
      })
    }
  }

  /**
   * Die Untereinträge eines Eintrags setzen — mit ihnen wird er zur Gruppe, ohne sie wieder zum
   * einfachen Eintrag. Eigene Aktion, weil `setOptionalNavigationItemData` leere Werte übergeht
   * und die Gruppe so nicht wieder wegnehmen kann.
   */
  function setOptionalNavigationItemChildren(payload: { name: string, children: OptionalNavigationItem[] }) {
    const eintrag = optionalNavigationItems.value.find(
      el => (el as OptionalNavigationItem).name === payload.name
    ) as OptionalNavigationItem | undefined
    if (!eintrag) {
      return
    }
    if (payload.children.length) {
      // Unter `uniqueName` klappt `BaseNavigation` die Gruppe auf.
      eintrag.uniqueName = payload.name
      eintrag.children = payload.children
    } else {
      delete eintrag.uniqueName
      delete eintrag.children
    }
  }

  // Navigation - Berichte
  function addBerichtInhaltsverzeichnisItems(payload: OptionalNavigationItem) {
    const index = optionalNavigationItems.value.findIndex(
      el => (el as OptionalNavigationItem).name === 'Inhaltsverzeichnis'
    )
    if (index === -1) {
      optionalNavigationItems.value.push({ type: 'item', ...payload })
    } else {
      optionalNavigationItems.value[index] = { type: 'item', ...payload }
    }
  }

  function replaceBerichtInhaltsverzeichnisItemText(payload: { uniqueName: string, text: string }) {
    const index = optionalNavigationItems.value.findIndex(
      el => (el as OptionalNavigationItem).name === 'Inhaltsverzeichnis'
    )
    if (index >= 0) {
      const subIndex = (optionalNavigationItems.value[index] as OptionalNavigationItem).children?.findIndex(
        el => el.uniqueName === payload.uniqueName
      )
      if (subIndex !== undefined && subIndex >= 0) {
        ((optionalNavigationItems.value[index] as OptionalNavigationItem).children as OptionalNavigationItem[])[subIndex].text = payload.text
      }
    }
  }

  function removeBerichtInhaltsverzeichnis() {
    const index = optionalNavigationItems.value.findIndex(
      el => (el as OptionalNavigationItem).name === 'Inhaltsverzeichnis'
    )
    if (index >= 0 && optionalNavigationItems.value[index]) {
      optionalNavigationItems.value.splice(index, 1)
    }
  }

  // Last Position
  function setLastPosition({ url, data }: { url: string, data: NavigationPosition }) {
    lastPosition.value[url] = data
  }

  function removeLastPosition(url: string) {
    delete lastPosition.value[url]
  }

  function setBrowserBackForwardUsed(payload: boolean) {
    browserBackForwardUsed.value = payload
  }

  // Getter
  const getLastPosition = computed(() => {
    return (url: string): NavigationPosition | null => {
      return lastPosition.value[url] || null
    }
  })

  return {
    // State
    navigationVisible,
    navigationItems,
    optionalNavigationItems,
    lastPosition,
    browserBackForwardUsed,
    // Actions
    setNavigationVisible,
    replaceNavigationItems,
    addOptionalNavigationItem,
    removeOptionalNavigationItem,
    clearOptionalNavigationItems,
    setOptionalNavigationItemData,
    setOptionalNavigationItemChildren,
    addBerichtInhaltsverzeichnisItems,
    replaceBerichtInhaltsverzeichnisItemText,
    removeBerichtInhaltsverzeichnis,
    setLastPosition,
    removeLastPosition,
    setBrowserBackForwardUsed,
    // Getter
    getLastPosition,
  }
})
